import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { wsNoteReviewAIUrl } from "utils/urls";

function createRequestId() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `note-review-ai-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useNoteReviewAIWebSocket({
    enabled = true,
    noteNo,
    onCompleted,
    onDelta,
    onError,
    onStarted,
    onStopped,
    spaceUuid,
}) {
    const wsRef = useRef(null);
    const pendingMessageRef = useRef(null);
    const currentRequestIdRef = useRef(null);
    const callbacksRef = useRef({});
    const [status, setStatus] = useState("idle");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        callbacksRef.current = {
            onCompleted,
            onDelta,
            onError,
            onStarted,
            onStopped,
        };
    }, [onCompleted, onDelta, onError, onStarted, onStopped]);

    const connectionReady = useMemo(() => enabled && !!spaceUuid && !!noteNo, [enabled, noteNo, spaceUuid]);

    const handleSocketMessage = useCallback((event) => {
        const data = JSON.parse(event.data || "{}");
        const type = data?.type;
        const payload = data?.payload || {};
        const requestId = payload?.request_id;
        if (requestId && requestId !== currentRequestIdRef.current) {
            return;
        }
        if (type === "review_ai.started") {
            setStatus("running");
            setErrorMessage("");
            callbacksRef.current.onStarted?.(payload);
            return;
        }
        if (type === "review_ai.delta") {
            callbacksRef.current.onDelta?.(payload);
            return;
        }
        if (type === "review_ai.completed") {
            setStatus("completed");
            callbacksRef.current.onCompleted?.(payload);
            return;
        }
        if (type === "review_ai.stopped") {
            setStatus("stopped");
            callbacksRef.current.onStopped?.(payload);
            return;
        }
        if (type === "review_ai.error") {
            setStatus("error");
            setErrorMessage(payload.message || "AI 리뷰 생성에 실패했습니다.");
            callbacksRef.current.onError?.(payload);
        }
    }, []);

    const connectWebSocket = useCallback(() => {
        if (!connectionReady) return;
        if (wsRef.current && [WebSocket.CONNECTING, WebSocket.OPEN].includes(wsRef.current.readyState)) {
            return;
        }
        const socket = new WebSocket(wsNoteReviewAIUrl(spaceUuid, noteNo));
        wsRef.current = socket;
        socket.onopen = () => {
            if (pendingMessageRef.current) {
                socket.send(JSON.stringify(pendingMessageRef.current));
                pendingMessageRef.current = null;
            }
        };
        socket.onmessage = handleSocketMessage;
        socket.onerror = () => {
            setErrorMessage("AI 리뷰 소켓 연결에 실패했습니다.");
        };
        socket.onclose = () => {
            wsRef.current = null;
        };
    }, [connectionReady, handleSocketMessage, noteNo, spaceUuid]);

    useEffect(() => {
        if (connectionReady) {
            connectWebSocket();
        }
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [connectWebSocket, connectionReady]);

    const sendOrQueue = useCallback(
        (message) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify(message));
                return;
            }
            pendingMessageRef.current = message;
            connectWebSocket();
        },
        [connectWebSocket],
    );

    const startRequest = useCallback(
        ({ messageId, threadId }) => {
            const requestId = createRequestId();
            currentRequestIdRef.current = requestId;
            setStatus("connecting");
            setErrorMessage("");
            sendOrQueue({
                action: "start",
                payload: {
                    request_id: requestId,
                    thread_id: threadId,
                    message_id: messageId,
                },
            });
            return requestId;
        },
        [sendOrQueue],
    );

    const stopRequest = useCallback(() => {
        if (!currentRequestIdRef.current) return;
        sendOrQueue({
            action: "stop",
            payload: {
                request_id: currentRequestIdRef.current,
            },
        });
    }, [sendOrQueue]);

    return {
        errorMessage,
        isRunning: status === "running" || status === "connecting",
        startRequest,
        status,
        stopRequest,
    };
}
